import React from 'react';
import classNames from 'classnames';

import css from './Track.module.css';

const TRACK_HORIZONTAL_INSET = 10;

/**
 * A component that renders a track for a range slider.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {ReactNode} props.children - The children to render
 * @param {Array<number>} props.handles - The handles to render
 * @param {Function} props.valueToPosition - The function to convert the value to the position
 * @returns {JSX.Element} the track component that has the handles and the range between them
 */
const Track = props => {
  const { rootClassName, className, children, handles = [], valueToPosition } = props;
  const positionFromIndex = index => valueToPosition(handles[index]);

  const classes = classNames(rootClassName || css.root, className);
  const rangeSegments =
    handles.length === 1
      ? (() => {
          const handlePosition = valueToPosition(handles[0]);
          return [
            <div
              key="range_min_to_handle"
              className={classNames(css.range, css.rangeFromStart)}
              style={{
                left: `${-TRACK_HORIZONTAL_INSET}px`,
                width: `${Math.max(
                  TRACK_HORIZONTAL_INSET,
                  handlePosition + TRACK_HORIZONTAL_INSET
                )}px`,
              }}
            />,
          ];
        })()
      : handles.reduce((ranges, h, index) => {
          return index < handles.length - 1
            ? [
                ...ranges,
                <div
                  key={`range_${index}-${index + 1}`}
                  className={css.range}
                  style={{
                    left: `${valueToPosition(h)}px`,
                    width: `${positionFromIndex(index + 1) - valueToPosition(h)}px`,
                  }}
                />,
              ]
            : ranges;
        }, []);

  return (
    <div className={classes}>
      <div className={css.track} />

      {rangeSegments}

      {children}
    </div>
  );
};

export default Track;
